import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { getDailyExpenseReport } from "../../core/api/reportService";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today() };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

// GET /api/reports/daily-expenses?fromDate=&toDate= (HANDOVER_FRONTEND 2.md) already merges
// Expense + Journal + Supplier Payment + Supplier Advance server-side — no client-side merge.
const ExpenseReport = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getDailyExpenseReport(f);
      setRows(res.data || []);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load report."), "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const totalAmount = rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0);

  // `bank` is the amount specifically paid via Bank mode — a UPI/Cash payment can still
  // be tagged to a bank (bankName present) with `bank` itself at 0, so bankName must be
  // shown independently of whether the bank-mode amount is non-zero.
  const bankCell = (r) => (r.bankName ? `${fmt(r.bank)} (${r.bankName})` : fmt(r.bank));

  const handleExport = () => {
    const headers = [
      "Sl.No", "Date", "Type", "Reference", "Particulars", "Category/Source",
      "Cash", "Card", "UPI", "Bank", "Bank Name", "Total Amount", "Remarks",
    ];
    const dataRows = rows.map((r) => [
      r.slNo, r.date ? r.date.split("T")[0] : "", r.type, r.reference, r.particulars, r.category,
      r.cash, r.card, r.uPI, r.bank, r.bankName || "", r.totalAmount, r.remarks || "",
    ]);
    dataRows.push(["", "", "", "", "", "TOTAL", "", "", "", "", "", totalAmount, ""]);
    exportToCsv(`Expense-Report-${filters.from}_to_${filters.to}.csv`, headers, dataRows);
  };

  // No explicit widths / ellipsis / scroll.x here — columns size naturally off their own
  // content (a narrow column like Sl.No stays narrow, a wide one like Particulars grows),
  // same as every other list page's Table in this app. The earlier "columns disappear"
  // symptom wasn't actually caused by natural sizing — it was the shared Table's rowKey
  // colliding across every row (fixed in datatable.jsx), which left stale/blank cells
  // behind after re-render regardless of column width.
  const columns = [
    { title: "Sl.No", dataIndex: "slNo" },
    { title: "Date", dataIndex: "date", render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Type", dataIndex: "type" },
    { title: "Reference", dataIndex: "reference" },
    { title: "Particulars", dataIndex: "particulars", render: (v) => v || "—" },
    { title: "Category/Source", dataIndex: "category" },
    { title: "Cash", dataIndex: "cash", align: "right", render: fmt },
    { title: "Card", dataIndex: "card", align: "right", render: fmt },
    { title: "UPI", dataIndex: "uPI", align: "right", render: fmt },
    { title: "Bank", dataIndex: "bank", render: (_, rec) => bankCell(rec) },
    { title: "Total Amount", dataIndex: "totalAmount", align: "right", render: fmt, sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0) },
    { title: "Remarks", dataIndex: "remarks", render: (v) => v || "—" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Expense Report</h4><h6>Expense, Journal, Supplier Payment &amp; Supplier Advance — combined view</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={!rows.length}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6 col-md-3 mb-2">
            <div className="card border-danger text-center py-2">
              <div className="text-danger fs-6 fw-bold">{fmt(totalAmount)}</div>
              <div className="small text-muted">Total Amount</div>
            </div>
          </div>
          <div className="col-6 col-md-3 mb-2">
            <div className="card border-secondary text-center py-2">
              <div className="text-secondary fs-6 fw-bold">{rows.length}</div>
              <div className="small text-muted">Entries</div>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={rows} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;
